// #215 — Pin-to-element selector engine.
//
// Pure function: given a clicked element, returns a CSS selector that
// resolves uniquely to that element (or as close as we can get). Priority
// chain: data-testid → data-cy/qa → other data-* → id → stable class
// combo → tag + role + structural position → nth-child path.
//
// Every strategy validates uniqueness via querySelectorAll. If a strategy
// produces a selector that isn't unique, we walk up parents adding ancestor
// prefixes (up to 5 levels) before falling through to the next strategy.
//
// Text content is intentionally NOT a strategy (PII risk + :has-text is
// Playwright-only, not standard CSS).
//
// See design: oncatch-wiki/wiki/decisions/2026-05-22-215-pin-to-element-design.md

const MAX_SELECTOR_LENGTH = 300;
const MAX_ANCESTOR_WALK = 5;

// Patterns for "obviously generated" class names that shouldn't anchor a
// selector. Covers the major frameworks. New frameworks: add a regex here.
const GENERATED_CLASS_PATTERNS: ReadonlyArray<RegExp> = [
  /^css-[a-z0-9]{4,}$/i, // emotion / styled-components
  /^_[a-z0-9]{3,}$/i, // CSS modules
  /^jss[0-9]+$/i, // Material-UI legacy
  /^[a-z]{2}-[a-z0-9]{6,}$/i, // Vite chunk hashes
  /^svelte-[a-z0-9]+$/i, // Svelte scoped
  /^_ngcontent-[a-z0-9-]+$/i, // Angular view encapsulation
  /^data-v-[a-f0-9]+$/i, // Vue scoped (as class)
  /^q[:-][a-z0-9_]+$/i, // Qwik markers
];

// Framework data-* attribute markers we should skip (they're internal,
// not stable across deploys).
const FRAMEWORK_DATA_ATTRS: ReadonlyArray<string> = [
  'data-reactid',
  'data-reactroot',
  'data-v',
  'data-svelte-h',
  'data-qwik-id',
];

export interface SelectorResult {
  selector: string;
  selector_confidence: 'high' | 'medium' | 'low';
  selector_queryable: boolean;
  inner_selector: string | null;
  inner_selector_queryable: boolean;
  inside_open_shadow: boolean;
  inside_closed_shadow: boolean;
}

export function generateSelector(element: Element): SelectorResult {
  const rootInfo = detectShadowContext(element);
  const queryRoot = rootInfo.queryRoot;

  let outerSelector: string;
  let confidence: 'high' | 'medium' | 'low';

  if (rootInfo.inside_open_shadow && rootInfo.host) {
    outerSelector = generateUniqueSelector(rootInfo.host, document).selector;
    confidence = generateUniqueSelector(rootInfo.host, document).confidence;
  } else if (rootInfo.inside_closed_shadow && rootInfo.host) {
    outerSelector = generateUniqueSelector(rootInfo.host, document).selector;
    confidence = generateUniqueSelector(rootInfo.host, document).confidence;
  } else {
    const generated = generateUniqueSelector(element, queryRoot);
    outerSelector = generated.selector;
    confidence = generated.confidence;
  }

  // Validate uniqueness of outer selector from document
  const outerQueryable = isUniqueIn(outerSelector, document, element, rootInfo);

  let innerSelector: string | null = null;
  let innerQueryable = false;

  if (rootInfo.inside_open_shadow && rootInfo.host?.shadowRoot) {
    const innerResult = generateUniqueSelector(element, rootInfo.host.shadowRoot);
    innerSelector = innerResult.selector;
    // Inner selector validates against the shadowRoot directly — NOT through
    // isUniqueIn (which always checks against document when shadow context is
    // set). The previous code falsely marked all valid inner selectors as
    // non-queryable.
    innerQueryable = isUniqueRaw(rootInfo.host.shadowRoot, innerSelector, element);
  }

  return {
    selector: outerSelector,
    selector_confidence: confidence,
    selector_queryable: outerQueryable,
    inner_selector: innerSelector,
    inner_selector_queryable: innerQueryable,
    inside_open_shadow: rootInfo.inside_open_shadow,
    inside_closed_shadow: rootInfo.inside_closed_shadow,
  };
}

interface ShadowInfo {
  inside_open_shadow: boolean;
  inside_closed_shadow: boolean;
  host: Element | null;
  queryRoot: Document | ShadowRoot;
}

function detectShadowContext(element: Element): ShadowInfo {
  const root = element.getRootNode();
  if (root === document) {
    return {
      inside_open_shadow: false,
      inside_closed_shadow: false,
      host: null,
      queryRoot: document,
    };
  }
  if (root instanceof ShadowRoot) {
    return {
      inside_open_shadow: root.mode === 'open',
      inside_closed_shadow: false,
      host: root.host,
      queryRoot: root,
    };
  }
  // Element's root is not document and not an accessible ShadowRoot —
  // very likely inside a closed shadow with the host as the deepest
  // visible element. Treat as closed-shadow case.
  return {
    inside_open_shadow: false,
    inside_closed_shadow: true,
    host: element,
    queryRoot: document,
  };
}

function generateUniqueSelector(
  element: Element,
  queryRoot: Document | ShadowRoot,
): { selector: string; confidence: 'high' | 'medium' | 'low' } {
  // Priority 1: data-testid
  const testid = element.getAttribute('data-testid');
  if (testid) {
    const sel = `[data-testid=${quote(testid)}]`;
    if (isUniqueRaw(queryRoot, sel, element)) return { selector: sel, confidence: 'high' };
    const climbed = climbToUnique(sel, element, queryRoot);
    if (climbed) return { selector: climbed, confidence: 'high' };
  }

  // Priority 2: data-cy or data-qa
  for (const attr of ['data-cy', 'data-qa']) {
    const value = element.getAttribute(attr);
    if (value) {
      const sel = `[${attr}=${quote(value)}]`;
      if (isUniqueRaw(queryRoot, sel, element)) return { selector: sel, confidence: 'high' };
      const climbed = climbToUnique(sel, element, queryRoot);
      if (climbed) return { selector: climbed, confidence: 'high' };
    }
  }

  // Priority 3: any non-framework data-* attribute
  for (const { name, value } of Array.from(element.attributes)) {
    if (!name.startsWith('data-')) continue;
    if (name === 'data-testid' || name === 'data-cy' || name === 'data-qa') continue;
    if (FRAMEWORK_DATA_ATTRS.some((f) => name === f || name.startsWith(`${f}-`))) continue;
    if (!value) continue;
    const sel = `[${name}=${quote(value)}]`;
    if (isUniqueRaw(queryRoot, sel, element)) return { selector: sel, confidence: 'medium' };
    const climbed = climbToUnique(sel, element, queryRoot);
    if (climbed) return { selector: climbed, confidence: 'medium' };
  }

  // Priority 4: id
  const id = element.id;
  if (id) {
    const sel = `#${cssEscape(id)}`;
    if (isUniqueRaw(queryRoot, sel, element)) return { selector: sel, confidence: 'high' };
    // ID exists but isn't unique (rare but happens); fall through.
  }

  // Priority 5: stable class combo on element. When siblings share the
  // same class set, the bare selector matches multiple elements; we try
  // ancestor climbing first, then add nth-of-type as a leaf disambiguator.
  const stableClasses = Array.from(element.classList).filter(isStableClass);
  if (stableClasses.length > 0) {
    const baseSel = `${tagPart(element)}.${stableClasses.map(cssEscape).join('.')}`;
    if (isUniqueRaw(queryRoot, baseSel, element)) {
      return { selector: baseSel, confidence: 'medium' };
    }
    const climbed = climbToUnique(baseSel, element, queryRoot);
    if (climbed) return { selector: climbed, confidence: 'medium' };
    // Leaf disambiguation via nth-of-type, then re-attempt climb if needed.
    const withNth = `${baseSel}:nth-of-type(${nthOfType(element)})`;
    if (isUniqueRaw(queryRoot, withNth, element)) {
      return { selector: withNth, confidence: 'medium' };
    }
    const climbedWithNth = climbToUnique(withNth, element, queryRoot);
    if (climbedWithNth) return { selector: climbedWithNth, confidence: 'medium' };
  }

  // Priority 6: tag + ARIA role + structural position
  const role = element.getAttribute('role');
  if (role) {
    const sel = `${tagPart(element)}[role=${quote(role)}]:nth-of-type(${nthOfType(element)})`;
    if (isUniqueRaw(queryRoot, sel, element)) return { selector: sel, confidence: 'low' };
    const climbed = climbToUnique(sel, element, queryRoot);
    if (climbed) return { selector: climbed, confidence: 'low' };
  }

  // Priority 7: nth-child path from nearest stable ancestor
  const path = nthChildPath(element, queryRoot);
  return { selector: path.selector, confidence: 'low' };
}

function climbToUnique(
  baseSelector: string,
  startElement: Element,
  queryRoot: Document | ShadowRoot,
): string | null {
  let current: Element | null = startElement.parentElement;
  let depth = 0;
  while (current && depth < MAX_ANCESTOR_WALK) {
    const ancestorSelector = generateAncestorPart(current);
    if (ancestorSelector) {
      const combined = `${ancestorSelector} ${baseSelector}`;
      if (
        combined.length <= MAX_SELECTOR_LENGTH &&
        isUniqueRaw(queryRoot, combined, startElement)
      ) {
        return combined;
      }
    }
    current = current.parentElement;
    depth += 1;
  }
  return null;
}

function generateAncestorPart(element: Element): string | null {
  // For ancestor prefix: prefer testid, then id, then stable class, then tag
  const testid = element.getAttribute('data-testid');
  if (testid) return `[data-testid=${quote(testid)}]`;
  if (element.id) return `#${cssEscape(element.id)}`;
  const stable = Array.from(element.classList).filter(isStableClass);
  if (stable.length > 0) return `${tagPart(element)}.${stable.map(cssEscape).join('.')}`;
  return null;
}

function nthChildPath(element: Element, queryRoot: Document | ShadowRoot): { selector: string } {
  const parts: string[] = [];
  let current: Element | null = element;

  while (current && parts.length < 10) {
    if (queryRoot instanceof Document) {
      if (current === queryRoot.documentElement) break;
    } else {
      // ShadowRoot: comparing Element to ShadowRoot is fine at the value level
      // even though TS flags the union; cast through unknown to keep this check.
      if ((current as unknown) === (queryRoot as unknown)) break;
    }
    const ancestorPrefix = generateAncestorPart(current);
    if (ancestorPrefix) {
      parts.unshift(ancestorPrefix);
      break;
    }
    parts.unshift(`${tagPart(current)}:nth-child(${nthChild(current)})`);
    current = current.parentElement;
  }
  // When no stable ancestor was found, parts[0] is the leftmost ancestor's
  // tag + nth-child (set by the loop). DO NOT replace it with the target's
  // tag — that produces impossible selectors like
  // `span > main > div > span` for a normal `main > div > span` tree.

  let combined = parts.join(' > ');
  if (combined.length > MAX_SELECTOR_LENGTH) {
    // Truncate from the front: keep the rightmost (most specific) part and
    // the highest-level stable ancestor we had.
    const kept = [parts[0], parts[parts.length - 1]].filter(Boolean) as string[];
    combined = kept.join(' > ');
  }
  return { selector: combined || tagPart(element) };
}

function tagPart(element: Element): string {
  return element.tagName.toLowerCase();
}

function nthChild(element: Element): number {
  if (!element.parentElement) return 1;
  return Array.from(element.parentElement.children).indexOf(element) + 1;
}

function nthOfType(element: Element): number {
  if (!element.parentElement) return 1;
  const tag = element.tagName;
  const sameTag = Array.from(element.parentElement.children).filter((c) => c.tagName === tag);
  return sameTag.indexOf(element) + 1;
}

function isStableClass(className: string): boolean {
  if (className.length > 25) return false;
  if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(className)) return false;
  if (GENERATED_CLASS_PATTERNS.some((p) => p.test(className))) return false;
  const digitCount = (className.match(/[0-9]/g) ?? []).length;
  if (digitCount > 3) return false;
  return true;
}

function quote(value: string): string {
  // Use double-quotes; escape backslashes and double-quotes inside.
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

function cssEscape(value: string): string {
  if (
    typeof (globalThis as { CSS?: { escape?: (s: string) => string } }).CSS?.escape === 'function'
  ) {
    return (globalThis as { CSS: { escape: (s: string) => string } }).CSS.escape(value);
  }
  // Fallback for environments without CSS.escape: escape non-alphanumeric.
  return value.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}

function isUniqueRaw(
  queryRoot: Document | ShadowRoot,
  selector: string,
  expectedElement: Element,
): boolean {
  if (selector.length > MAX_SELECTOR_LENGTH) return false;
  try {
    const found = queryRoot.querySelectorAll(selector);
    return found.length === 1 && found[0] === expectedElement;
  } catch {
    // Invalid selector — should not happen if we escape correctly, but
    // defensive against future strategy bugs.
    return false;
  }
}

function isUniqueIn(
  selector: string,
  queryRoot: Document | ShadowRoot,
  expectedElement: Element,
  rootInfo: ShadowInfo,
): boolean {
  // For shadow-DOM elements, "unique from document" means the host
  // selector must resolve to the host. expectedElement might be the
  // host (closed shadow case) or the actual element (no shadow).
  if (rootInfo.inside_open_shadow || rootInfo.inside_closed_shadow) {
    if (!rootInfo.host) return false;
    return isUniqueRaw(document, selector, rootInfo.host);
  }
  return isUniqueRaw(queryRoot, selector, expectedElement);
}
